import { isNumeric } from '../../util/helpers';
import { LatLon } from '../../util/geodesy/latlon';
import { parseLatLon } from '../util/parseUtils';


/**
 * Navaid type
 */
export enum NavaidType {
  MarineNDB,
  VOR_DME,
  TACAN,
  VOT,
  NDB,
  NDB_DME,
  VORTAC,
  VOR,
  DME
}


/**
 * Data model for a navaid near an airport. This class stores basic summary information about the navaid (including the
 * identifier, name, and type), as well as the distance and radial of the navaid relative to the airport.
 */
export class AirportNavaid {
  /**
   * ID of the record in the database that identifies this navaid
   */
  navaidId: number;

  /**
   * Navaid identifier
   */
  identifier: string;

  /**
   * Navaid name
   */
  name: string;

  /**
   * Navaid type
   */
  type: NavaidType;

  /**
   * Navaid frequency
   */
  frequency: string;

  /**
   * Distance of the navaid from the airport (in nautical miles)
   */
  distance: number;

  /**
   * Navaid radial (in degrees) that the airport is on
   */
  radial: number;

  /**
   * Navaid location
   */
  location: LatLon;

  /**
   * Navaid Magvar
   */
  magvar: number;

  /**
   * Navaid Status
   */
  status: string;

  /**
   * Navaid Elevation
   */
  elevation?: number;

  /**
   * Navaid City
   */
  city?: string;

  /**
   * Navaid State
   */
  state?: string;

  /**
   * Navaid Country
   */
  country?: string;

  /**
   * Navaid Source Id
   */
  sourceId: number;

  /**
   * Parses navaid
   * @param data {any} Navaid data as returned by API
   * @return {AirportNavaid} Parsed navaid
   */
  static parse(data: any): AirportNavaid {
    const navaidData = data.navaid || {};
    return {
      navaidId: data.navaidId,
      identifier: data.identifier,
      name: navaidData.name,
      type: this.parseNavaidType(data.type),
      frequency: navaidData.frequency,
      distance: isNumeric(data.distance) ? +data.distance : null,
      radial: isNumeric(data.radial) ? +data.radial : null,
      location: navaidData.location ? parseLatLon(navaidData.location.latitude, navaidData.location.longitude) : null,
      magvar: navaidData.magvar,
      status: navaidData.status,
      elevation: navaidData.elevation,
      city: navaidData.city,
      state: navaidData.state,
      country: navaidData.country,
      sourceId: navaidData.sourceId
    };
  }

  /**
   * Parses navaid type
   * @param value {any} Navaid type as returned by API
   * @return {NavaidType} Parsed navaid type
   */
  static parseNavaidType(value: any): NavaidType {
    switch (value) {
      case 'MARINE NDB':
        return NavaidType.MarineNDB;
      case 'VOR/DME':
        return NavaidType.VOR_DME;
      case 'TACAN':
        return NavaidType.TACAN;
      case 'VOT':
        return NavaidType.VOT;
      case 'NDB':
        return NavaidType.NDB;
      case 'NDB/DME':
        return NavaidType.NDB_DME;
      case 'VORTAC':
        return NavaidType.VORTAC;
      case 'VOR':
        return NavaidType.VOR;
      case 'DME':
        return NavaidType.DME;
      default:
        return null;
    }
  }

  /**
   * Formats navaid type for display
   * @param value {NavaidType} Navaid type
   * @return {string} Formatted value for display
   */
  static formatNavaidType(value: NavaidType): string {
    switch (value) {
      case NavaidType.MarineNDB:
        return 'MARINE NDB';
      case NavaidType.VOR_DME:
        return 'VOR/DME';
      case NavaidType.TACAN:
        return 'TACAN';
      case NavaidType.VOT:
        return 'VOT';
      case NavaidType.NDB:
        return 'NDB';
      case NavaidType.NDB_DME:
        return 'NDB/DME';
      case NavaidType.VORTAC:
        return 'VORTAC';
      case NavaidType.VOR:
        return 'VOR';
      case NavaidType.DME:
        return 'DME';
      default:
        return null;
    }
  }
}
